import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingBag, Settings, LogOut, Users,
  FileText, Truck, Download, ChevronLeft, CreditCard, RefreshCcw,
  Building2, MessageSquare, Tag, RotateCcw, Shield, Mail,
  AlertTriangle, Bell, FileQuestion, PanelLeftClose, PanelLeftOpen, Link2, Receipt
} from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { Button } from "../ui/button";
import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import PinnacleStatusIndicator from "../PinnacleStatusIndicator";
import { useSettings } from "../../context/SettingsContext";

/* ─── Sidebar sections with permission keys ─────────────────────── */
const SIDEBAR_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard" },
    ],
  },
  {
    label: "Management",
    items: [
      { to: "/admin/products",  label: "Inventory",  icon: Package,    perm: "inventory" },
      { to: "/admin/orders",    label: "Orders",     icon: ShoppingBag, perm: "orders" },
      { to: "/admin/customers", label: "Customers",  icon: Users,      perm: "customers" },
      { to: "/admin/credits",   label: "Credits",    icon: CreditCard, perm: "credits" },
      { to: "/admin/payment-links", label: "Payment Links", icon: Link2, perm: "invoices" },
      { to: "/admin/invoices",  label: "Invoices",   icon: FileText,   perm: "invoices" },
      { to: "/admin/price-quotes", label: "Quotes",  icon: Receipt,    perm: "invoices" },
      { to: "/admin/shipping",  label: "Shipping",   icon: Truck,      perm: "shipping" },
    ],
  },
  {
    label: "Requests",
    items: [
      { to: "/admin/quotes",   label: "Quote Requests", icon: FileQuestion,  badgeKey: "quotes",    perm: "requests" },
      { to: "/admin/offers",   label: "Offers",    icon: Tag,           badgeKey: "offers",    perm: "requests" },
      { to: "/admin/returns",  label: "Returns",   icon: RotateCcw,    badgeKey: "returns",   perm: "requests" },
      { to: "/admin/b2b",      label: "B2B Trade", icon: Building2,    badgeKey: "b2b",       perm: "requests" },
      { to: "/admin/messages", label: "Messages",  icon: MessageSquare, badgeKey: "messages", perm: "messages" },
    ],
  },
  {
    label: "Sync",
    items: [
      { to: "/admin/sync-queue", label: "Sync Queue",    icon: RefreshCcw,    badgeKey: "queue",     perm: "sync" },
      { to: "/admin/sync",       label: "Import/Export", icon: Download,                             perm: "sync" },
      { to: "/admin/conflicts",  label: "Conflicts",     icon: AlertTriangle, badgeKey: "conflicts", perm: "sync" },
      { to: "/admin/audit-log",  label: "Audit Log",     icon: Shield,                               perm: "audit" },
      { to: "/admin/email-log",  label: "Email Log",     icon: Mail,                                 perm: "audit" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/settings", label: "Settings", icon: Settings, perm: "settings" },
    ],
  },
];

const FRESH_BADGES = { quotes: 0, messages: 0, offers: 0, b2b: 0, returns: 0, queue: 0, conflicts: 0 };

export default function AdminLayout() {
  const { admin, loading, logout, hasPermission } = useAdminAuth();
  const { settings } = useSettings();
  const navigate      = useNavigate();
  const [badges,    setBadges]    = useState(FRESH_BADGES);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bcp_sidebar_collapsed") === "1");

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login", { replace: true });
  }, [loading, admin, navigate]);

  useEffect(() => {
    localStorage.setItem("bcp_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (!admin) return;
    const load = async () => {
      try {
        const [q, m, o, b, r, p, cf] = await Promise.all([
          adminApi.get("/admin/quotes").catch(() => ({ data: {} })),
          adminApi.get("/admin/messages").catch(() => ({ data: {} })),
          adminApi.get("/admin/offers").catch(() => ({ data: {} })),
          adminApi.get("/admin/b2b-requests").catch(() => ({ data: {} })),
          adminApi.get("/admin/returns").catch(() => ({ data: {} })),
          adminApi.get("/pinnacle/status").catch(() => ({ data: {} })),
          adminApi.get("/admin/conflicts?status=open&limit=1").catch(() => ({ data: {} })),
        ]);
        setBadges({
          quotes:    q.data.pending       || 0,
          messages:  m.data.stats?.unread || 0,
          offers:    o.data.pending       || 0,
          b2b:       b.data.pending       || 0,
          returns:   r.data.pending       || 0,
          queue:     p.data.pending       || 0,
          conflicts: cf.data.stats?.open  || 0,
        });
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [admin]);

  if (loading || !admin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#eef2f8" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-9 h-9 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin" />
          <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#64748b" }}>
            Loading admin…
          </div>
        </div>
      </div>
    );
  }

  const totalAlerts = Object.values(badges).reduce((a, b) => a + b, 0);

  return (
    <div className="flex admin-bg" style={{ minHeight: "100vh" }} data-testid="admin-layout">

      {/* ── SIDEBAR — sticky h-screen so bottom is always visible ─────
          Key: sticky top-0 h-screen on aside, flex-1 overflow-y-auto on nav
      ── */}
      <aside
        style={{ background: "#0b1a3b", width: collapsed ? 64 : 220 }}
        className="sticky top-0 h-screen flex flex-col shrink-0 transition-[width] duration-200 border-r border-white/8 z-20"
        data-testid="admin-sidebar"
      >
        {/* Logo — fixed top, no shrink */}
        <Link
          to="/admin"
          className="flex items-center gap-2.5 border-b border-white/10 shrink-0 overflow-hidden"
          style={{ padding: collapsed ? "14px 12px" : "14px 16px", justifyContent: collapsed ? "center" : undefined }}
        >
          <img
            src={settings.logo_url || "/logo.png"}
            alt="BCP"
            className="object-contain bg-white rounded-lg shrink-0"
            style={{ width: 34, height: 34, padding: 4 }}
            onError={(e) => { e.target.src = "/logo.png"; }}
          />
          {!collapsed && (
            <div style={{ overflow: "hidden" }}>
              <div className="font-heading font-bold text-white truncate" style={{ fontSize: 13 }}>
                {settings.store_name ? settings.store_name.split(" ")[0] + " Admin" : "BCP Admin"}
              </div>
              <div className="font-mono text-blue-300/70 uppercase" style={{ fontSize: 9, letterSpacing: "0.18em" }}>
                Admin Portal
              </div>
            </div>
          )}
        </Link>

        {/* Navigation — scrollable, takes remaining height between logo and bottom */}
        <nav className="flex-1 overflow-y-auto admin-scroll py-2">
          {SIDEBAR_SECTIONS.map((section) => {
            const visible = section.items.filter(i => !i.perm || hasPermission(i.perm));
            if (!visible.length) return null;
            return (
            <div key={section.label} style={{ marginBottom: collapsed ? 4 : 12 }}>
              {!collapsed && (
                <div className="font-mono uppercase font-bold"
                  style={{ fontSize: 9, letterSpacing: "0.25em", color: "rgba(147,197,253,0.45)", padding: "0 16px", marginBottom: 4 }}>
                  {section.label}
                </div>
              )}
              {visible.map((item) => {
                const Icon = item.icon;
                const badgeCount = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                return (
                  <NavLink key={item.to} to={item.to} end={item.end}
                    title={collapsed ? item.label : undefined}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s|\//g, "-")}`}
                    className={({ isActive }) =>
                      `mx-2 flex items-center gap-2 rounded-lg transition-all text-[12.5px] relative ${
                        collapsed ? "justify-center px-0" : "px-3"
                      } ${isActive ? "admin-active font-semibold" : "text-blue-100/55 hover:text-white hover:bg-white/8"}`
                    }
                    style={{ height: 34 }}>
                    {Icon && <Icon style={{ width: 15, height: 15 }} className="shrink-0" />}
                    {!collapsed && (
                      <>
                        <span className="flex-1 font-medium">{item.label}</span>
                        {badgeCount > 0 && (
                          <span className="font-bold text-white rounded-full flex items-center justify-center leading-none"
                            style={{ minWidth: 17, height: 17, padding: "0 4px", background: "#e31e24", fontSize: 9 }}>
                            {badgeCount}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && badgeCount > 0 && (
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#e31e24]" />
                    )}
                  </NavLink>
                );
              })}
            </div>
            );
          })}
        </nav>

        {/* Bottom — shrink-0 so it never gets pushed off screen */}
        <div className="shrink-0 border-t border-white/10 p-2">
          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed((v) => !v)}
            data-testid="admin-sidebar-toggle"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="w-full flex items-center justify-center text-blue-200/40 hover:text-white border border-white/10 rounded-lg hover:bg-white/8 mb-2 transition-colors"
            style={{ height: 30 }}
          >
            {collapsed
              ? <PanelLeftOpen style={{ width: 14, height: 14 }} />
              : <PanelLeftClose style={{ width: 14, height: 14 }} />
            }
          </button>

          {/* User info — only expanded */}
          {!collapsed && (
            <div className="px-1 mb-2">
              <div className="font-mono text-blue-200/45 truncate" style={{ fontSize: 10 }}>{admin.email}</div>
              <Link to="/" className="flex items-center gap-1.5 text-blue-200/45 hover:text-white transition-colors" style={{ fontSize: 11 }}>
                <Building2 style={{ width: 11, height: 11 }} /> View Store
              </Link>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            data-testid="admin-logout-button"
            title="Log out"
            className="w-full flex items-center justify-center gap-1.5 text-blue-100/40 hover:text-red-300 border border-white/8 rounded-lg hover:bg-red-500/10 hover:border-red-500/30 transition-all"
            style={{ height: 30, fontSize: 11 }}
          >
            <LogOut style={{ width: 13, height: 13 }} />
            {!collapsed && <span className="font-mono uppercase tracking-wide font-semibold">Log out</span>}
          </button>
        </div>
      </aside>

      {/* ── MAIN AREA ────────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col min-h-screen overflow-x-auto">

        {/* Top bar */}
        <div
          className="sticky top-0 z-30 flex items-center justify-between px-6 md:px-8 shrink-0"
          style={{ background: "#ffffff", borderBottom: "1px solid #dde3ef", height: 56 }}
        >
          {/* Left */}
          <div className="flex items-center gap-3">
            <span className="font-heading font-bold" style={{ color: "#0f172a", fontSize: 16 }}>
              Admin Console
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full font-mono font-bold uppercase"
              style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #86efac", padding: "2px 10px", fontSize: 10, letterSpacing: "0.1em" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 pulse-dot" />
              Live
            </span>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {badges.queue > 0 && (
              <span className="hidden md:inline-flex items-center gap-1.5 rounded-lg font-mono font-semibold uppercase"
                style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", padding: "0 10px", height: 28, fontSize: 11, letterSpacing: "0.06em" }}>
                <RefreshCcw style={{ width: 11, height: 11 }} /> {badges.queue}
              </span>
            )}
            {badges.messages > 0 && (
              <span className="hidden md:inline-flex items-center gap-1.5 rounded-lg font-mono font-semibold uppercase"
                style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", padding: "0 10px", height: 28, fontSize: 11, letterSpacing: "0.06em" }}>
                <MessageSquare style={{ width: 11, height: 11 }} /> {badges.messages}
              </span>
            )}
            <PinnacleStatusIndicator />
            <button
              className="relative flex items-center justify-center rounded-lg transition-colors"
              style={{ width: 36, height: 36, border: "1px solid #dde3ef", color: "#475569" }}
            >
              <Bell style={{ width: 15, height: 15 }} />
              {totalAlerts > 0 && (
                <span
                  className="absolute flex items-center justify-center text-white font-bold rounded-full"
                  style={{ top: -4, right: -4, width: 16, height: 16, background: "#e31e24", fontSize: 9 }}
                >
                  {Math.min(totalAlerts, 9)}
                </span>
              )}
            </button>
            <div
              className="hidden lg:flex items-center gap-2 ml-1 pl-3"
              style={{ borderLeft: "1px solid #dde3ef" }}
            >
              <div
                className="flex items-center justify-center text-white font-bold rounded-lg shrink-0"
                style={{ width: 32, height: 32, background: "#0b1a3b", fontSize: 12 }}
              >
                {(admin.name || admin.email || "A")[0].toUpperCase()}
              </div>
              <div>
                <div className="font-semibold leading-none" style={{ fontSize: 12, color: "#0f172a" }}>
                  {admin.name || "Admin"}
                </div>
                <div className="font-mono mt-0.5" style={{ fontSize: 10, color: "#94a3b8" }}>BCP Admin</div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 p-6 md:p-8 admin-main">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export { Download };